import React, { useEffect, useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { APPROVAL, CUSTOM_BOX, DEFAULT_ADDRESS, LISTING_ADDRESS, LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL, PRE_DEFINED_BOX, propTypes } from '../../../../util/types';

// Import shared components
import {
  Form,
  Button,
  FieldTextInput,
  NamedLink,
  FieldLocationAutocompleteInput,
  IconCollection,
} from '../../../../components';

// Import modules from this directory
import css from './EditListingDeliveryForm.module.css';
import { boxSize } from '../../../../config/configSearch';
import FieldMultiSelect from '../../../../components/FieldMultiSelect/FieldMultiSelect';
import { autocompletePlaceSelected, autocompleteSearchRequired, composeValidators, phoneNumberFormatValid, required } from '../../../../util/validators';
import usStateCodes from 'us-state-codes';

const onCheckShippingAddress = currentUser => {
  const { location } = currentUser?.attributes?.profile?.protectedData || {};
  const { apartment, city, postalCode, state, origin, address } = location || {};
  return city && address && origin && origin.lat && postalCode && state ? false : true;
};

const identity = v => v;

export const EditListingDeliveryFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        formId,
        form,
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        pristine,
        invalid,
        saveActionMsg,
        updated,
        updateInProgress,
        values,
        currentUser,
        listing,
        onSaveDraft,
        handlePublishListing,
        history,
        setSaveAsDraft,
        saveAsDraft,
        onAddressValidationWithShippo,
        setAddressType,
        addressType,
        validateAddressInProgress
      } = formRenderProps;

      const { boxSizes, packagingType } =
        listing.attributes.publicData || {};
      const { height, length, width, weight } = boxSizes || {};
      const [isPredefined, setIsPreDefined] = useState(packagingType || PRE_DEFINED_BOX);
      const { email, firstName } = currentUser?.attributes?.profile?.publicData || {};
      const isPendingApproval = [LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_DRAFT].includes(listing?.id && listing?.attributes.state);

      // This is a bug fix for Final Form.
      // Without this, React will return a warning:
      //   "Cannot update a component (`ForwardRef(Field)`)
      //   while rendering a different component (`ForwardRef(Field)`)"
      // This seems to happen because validation calls listeneres and
      // that causes state to change inside final-form.
      // https://github.com/final-form/react-final-form/issues/751
      //
      // TODO: it might not be worth the trouble to show these fields as disabled,
      // if this fix causes trouble in future dependency updates.
      const { pauseValidation, resumeValidation } = form;
      pauseValidation(false);
      useEffect(() => resumeValidation(), [values]);

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress && !saveAsDraft;
      const isCustomBox = values?.packagingType == CUSTOM_BOX ?
        values.packageHeight && values.packageLength && values.packageWidth && values.packageWeight
        : values?.boxSizes && values?.packagePreDefinedWeight;
      const addressCheck = (addressType == LISTING_ADDRESS) ? !values?.location : null
      const submitDisabled =
        invalid ||
        disabled ||
        submitInProgress ||
        validateAddressInProgress ||
        !values.packagingType || !isCustomBox || addressCheck || (values.packageWeight > 30) || (values?.packagePreDefinedWeight > 30);


      useEffect(() => {
        form.change('packagingType', isPredefined);
      }, [isPredefined]);

      useEffect(() => {
        if (values?.packagingType === PRE_DEFINED_BOX) {
          form.batch(() => {
            form.change('packageLength', values?.boxSizes?.length);
            form.change('packageWidth', values?.boxSizes?.width);
            form.change('packageHeight', values?.boxSizes?.height);
            form.change('packageWeight', values?.boxSizes?.weight);
          })
        }
      }, [values?.boxSizes]);

      const handlePacking = value => {
        setIsPreDefined(value);
        if (value === CUSTOM_BOX) {
          form.batch(() => {
            form.change('packageLength', length);
            form.change('packageWidth', width);
            form.change('packageHeight', height);
            form.change('packageWeight', weight);
          })
        }
      };

      const checkShippingAddress = onCheckShippingAddress(currentUser);

      const saveDraftFunction = () => {
        const {
          boxSizes,
          packageWeight,
          packagingType,
          packagePreDefinedWeight,
          packageLength,
          packageHeight,
          packageWidth,
          location,
          postalCode,
          state,
          apartment,
          city,
          poBoxNumber,
          addressLine1,
          addressLine2,
        } = values;

        const { address, origin } = location?.selectedPlace || {};
        const { search } = location || {};

        const updateValues = {
          geolocation: origin,
          publicData: {
            boxSizes:
              packagingType === CUSTOM_BOX
                ? {
                  length: packageLength,
                  height: packageHeight,
                  width: packageWidth,
                  weight: packageWeight,
                }
                : { ...boxSizes, weight: packagePreDefinedWeight },
            packagingType,
            packagePreDefinedWeight: packagingType === PRE_DEFINED_BOX ? packagePreDefinedWeight : null,
            shippingEnabled: true,
            updatedAt: Math.floor(Date.now() / 1000),
            poBoxNumber,
            listingLocation: (addressType == LISTING_ADDRESS) ? {
              address: address || search,
              postalCode,
              state,
              apartment,
              city,
              country: 'US',
              poBoxNumber,
            } : null,
            addressType
          },
        };
        if((addressType == LISTING_ADDRESS)){
          return onAddressValidationWithShippo({
            postalCode,
            state,
            city,
            addressLine1,
            addressLine2: addressLine2 || apartment,
            email,
            firstName,
          }).then(() => {
            return onSaveDraft(updateValues);
          });

        } else {
          return onSaveDraft(updateValues);
        }
      };

      const addressRequiredMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.addressRequired',
      });

      const addressNotRecognizedMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.addressNotRecognized',
      });

      const phoneNumberValid = phoneNumberFormatValid(
        intl.formatMessage({
          id: 'ProfileSettingsForm.phoneNumberValid',
        })
      );

      // phoneNumber
      const phoneNumberRequired = required(
        intl.formatMessage({
          id: 'ProfileSettingsForm.phoneNumberRequired',
        })
      );

      useEffect(() => {
        const parseAddress = address => {
          // Split and trim each part to avoid issues with extra spaces
          const parts = address?.split(',').map(part => part.trim());

          // Assume the last three values are City, State Zip, and Country
          const locationInfo = parts.slice(-3);
          const addressLines = parts.slice(0, -3); // Address lines are everything before city, state, zip, country

          return { addressLines, locationInfo };
        };

        const extractStateAndZip = stateZip => {
          // Assuming US ZIP codes are 5 digits and potentially followed by a hyphen and 4 digits
          const match = stateZip.match(/(.*?)(\d{5})(-\d{4})?$/);
          if (!match) return {}; // In case the regex does not match

          const [_, stateName, zip] = match;
          return { stateName: stateName.trim(), zip };
        };

        const { addressLines, locationInfo } = parseAddress(values?.location?.search || '');

        if (locationInfo.length >= 3) {
          // Ensure we have City, State Zip, and Country
          const { stateName, zip } = extractStateAndZip(locationInfo[1]);

          // Extract address lines 1 and 2, or leave blank if not present
          const addressLine1 = addressLines[0] || '';
          const addressLine2 = addressLines[1] || '';

          form.change('addressLine1', addressLine1);
          form.change('addressLine2', addressLine2);
          form.change('city', locationInfo[0]);
          form.change('postalCode', zip);
          form.change('state', usStateCodes.getStateCodeByStateName(stateName) || stateName);
        }
      }, [values?.location?.search, form]);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <div>
              <div className={css.tabs}>
                <span
                  className={classNames(
                    addressType === DEFAULT_ADDRESS && css.tabItemActive,
                    css.tabItem
                  )}
                  onClick={() => setAddressType(DEFAULT_ADDRESS)}
                >
                  <FormattedMessage id="EditListingDeliveryForm.defaultAddress" />
                </span>
                <span
                  className={classNames(
                    addressType !== DEFAULT_ADDRESS && css.tabItemActive,
                    css.tabItem
                  )}
                  onClick={() => setAddressType(LISTING_ADDRESS)}
                >
                  <FormattedMessage id="EditListingDeliveryForm.shippingAddress" />
                </span>
              </div>
              {(addressType === DEFAULT_ADDRESS) ? <> {!checkShippingAddress ? (
                <div>
                  <h6 className={css.shippingLabel}>
                    <label>
                      <div className={css.priceLabel}>
                        <div className={css.customTooltip}>
                          <IconCollection icon="infoIconSquare" />
                          <div className={css.tooltipContent}>
                            <span> <IconCollection icon="infoIconWhite" /> </span>
                            <div>
                              <span><b><FormattedMessage id="EditListingDeliveryForm.defaultToolTipTitle" /></b></span><br /><br />
                              <span><FormattedMessage id="EditListingDeliveryForm.defaultToolTipContent" /></span>
                            </div>
                          </div>
                        </div>
                        <FormattedMessage
                          id="EditListingDeliveryForm.addressHeading2"
                          values={{ highLightText: <b>Shipping From</b> }}
                        />
                      </div>
                    </label>
                    <span className={css.address}>
                      <IconCollection icon="locationIcon" />
                      {currentUser?.attributes?.profile?.protectedData?.location?.address}
                    </span>
                    <span className={css.infoMessage}>
                      {' '}
                      <FormattedMessage id="EditListingDeliveryForm.addressChange" />
                    </span>
                  </h6>
                </div>
              ) : (
                <div>
                  <h6 className={css.shippingLabelNotVerified}>
                    <IconCollection icon="infoIconSquare" />&nbsp;<FormattedMessage id="EditListingDeliveryForm.addressHeading" values={{ shippingFrom: <b>Shipping From</b> }} />
                  </h6>
                  <NamedLink name="ProfileSettingsPage" className={css.settingsBtn}>
                    <IconCollection icon="settingIcon" />
                    <span>
                      <FormattedMessage id="EditListingDeliveryForm.gotToSetting" />
                    </span>
                  </NamedLink><br />
                  <span className={css.refreshPage}>
                    <FormattedMessage id="EditListingDeliveryForm.refreshPage" />
                  </span>
                </div>
              )}</> : <div>
                <label>
                  <div className={css.priceLabel}>
                    <div className={css.customTooltip}>
                      <IconCollection icon="infoIconSquare" />
                      <div className={css.tooltipContent}>
                        <span> <IconCollection icon="infoIconWhite" /> </span>
                        <div>
                          <span><b><FormattedMessage id="EditListingDeliveryForm.shippingToolTipTitle" /></b></span><br /><br />
                          <span><FormattedMessage id="EditListingDeliveryForm.shippingToolTipContent" /></span>
                        </div>
                      </div>
                    </div>
                    <FormattedMessage
                      id="EditListingDeliveryForm.addressHeading2"
                      values={{ highLightText: <b>Shipping From</b> }}
                    />
                  </div>
                </label>
                <FieldLocationAutocompleteInput
                  className={css.locationBox}
                  rootClassName={css.locationAddress}
                  inputClassName={css.locationAutocompleteInput}
                  iconClassName={css.locationAutocompleteInputIcon}
                  predictionsClassName={css.predictionsRoot}
                  validClassName={css.validLocation}
                  name="location"
                  label={intl.formatMessage({ id: 'ProfileSettingsForm.addressLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'ProfileSettingsForm.addressPlaceholder',
                  })}
                  useDefaultPredictions={false}
                  format={identity}
                  valueFromForm={values?.location}
                  validate={composeValidators(
                    autocompleteSearchRequired(addressRequiredMessage),
                    autocompletePlaceSelected(addressNotRecognizedMessage)
                  )}
                /><br />

                <div className={css.fieldSelect}>
                  <FieldTextInput
                    className={css.input}
                    type="text"
                    id="apartment"
                    name="apartment"
                    label={intl.formatMessage({ id: 'ProfileSettingsForm.appartmentLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.appartmentPlaceholder',
                    })}

                  />
                  <FieldTextInput
                    className={css.input}
                    type="text"
                    id="city"
                    name="city"
                    label={intl.formatMessage({ id: 'ProfileSettingsForm.cityLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.cityPlaceholder',
                    })}
                    validate={required(
                      intl.formatMessage({ id: 'ProfileSettingsForm.cityValidate' })
                    )}
                  />
                </div>
                <div className={css.fieldSelect}>
                  <FieldTextInput
                    className={css.input}
                    type="text"
                    id="state"
                    name="state"
                    label={intl.formatMessage({ id: 'ProfileSettingsForm.stateLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.statePlaceholder',
                    })}
                    validate={required(
                      intl.formatMessage({ id: 'ProfileSettingsForm.stateValidate' })
                    )}
                  />

                  <FieldTextInput
                    className={css.input}
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    label={intl.formatMessage({ id: 'ProfileSettingsForm.postalCodeLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.postalCodePlaceholder',
                    })}
                    validate={required(
                      intl.formatMessage({ id: 'ProfileSettingsForm.postalCodeValidate' })
                    )}
                  />
                </div>
                <div className={css.fieldSelect}>
                  <FieldTextInput
                    className={css.password}
                    id={`poBoxNumber`}
                    name="poBoxNumber"
                    type="text"
                    label={intl.formatMessage({ id: 'ShippingDetails.poBoxNumberLabel' })}

                  />
                </div><br />
              </div>}


            </div>
            <div>
              <h6 className={css.pricingLabel}>
                <FormattedMessage id="EditListingDeliveryForm.packingHeading" />
              </h6>
              <p className={css.packingSubHeading}>
                <FormattedMessage id="EditListingDeliveryForm.packingSubHeading" values={{
                  faqLink: <NamedLink name="CMSPage"
                    params={{ pageId: "faq" }}>FAQ Page</NamedLink>
                }} />

              </p>
              <div className={css.tabs}>
                <span
                  className={classNames(
                    values.packagingType !== CUSTOM_BOX && css.tabItemActive,
                    css.tabItem
                  )}
                  onClick={() => handlePacking(PRE_DEFINED_BOX)}
                >
                  <FormattedMessage id="EditListingDeliveryForm.predefinedBox" />
                </span>
                <span
                  className={classNames(
                    values.packagingType === CUSTOM_BOX && css.tabItemActive,
                    css.tabItem
                  )}
                  onClick={() => handlePacking(CUSTOM_BOX)}
                >
                  <FormattedMessage id="EditListingDeliveryForm.customBox" />
                </span>
              </div>
            </div>
            {values.packagingType === CUSTOM_BOX ? (
              <div>
                <FieldTextInput
                  className={css.input}
                  type="number"
                  name="packageLength"
                  id={`${formId}packageLength`}
                  label={intl.formatMessage({ id: 'EditListingDeliveryForm.packageLength' })}
                />
                <FieldTextInput
                  className={css.input}
                  type="number"
                  name="packageHeight"
                  id={`${formId}packageHeight`}
                  label={intl.formatMessage({ id: 'EditListingDeliveryForm.packageHeight' })}
                />
                <FieldTextInput
                  className={css.input}
                  type="number"
                  name="packageWidth"
                  id={`${formId}packageWidth`}
                  label={intl.formatMessage({ id: 'EditListingDeliveryForm.packageWidth' })}
                />
                <FieldTextInput
                  className={css.input}
                  type="number"
                  name="packageWeight"
                  max={30}
                  id={`${formId}packageWeight`}
                  label={intl.formatMessage({ id: 'EditListingDeliveryForm.packageweight' })}
                  validate={(packagingType == CUSTOM_BOX) && required(intl.formatMessage({ id: 'EditListingDeliveryForm.packageweightRequired' }))}
                />
              </div>
            ) : (
              <div className={css.fieldSelect}>
                <div className={css.input}>
                  <label>
                    <FormattedMessage id="EditListingDeliveryForm.boxSizes" />
                  </label>
                  <FieldMultiSelect
                    isMulti={false}
                    isRequired={true}
                    className={css.input}
                    id={'boxSizes'}
                    name={'boxSizes'}
                    fontClass={css.fontClass}
                    options={boxSize}
                    autoComplete={'boxSizes'}
                    defaultValue={values?.style || ''}
                  // validate={subCategoryRequired}
                  />
                </div>

                <FieldTextInput
                  className={css.input}
                  type="number"
                  name="packagePreDefinedWeight"
                  max={30}
                  id={`${formId}packagePreDefinedWeight`}
                  label={intl.formatMessage({ id: 'EditListingDeliveryForm.packageweight' })}
                  validate={(packagingType == PRE_DEFINED_BOX) && required(intl.formatMessage({ id: 'EditListingDeliveryForm.packageweightRequired' }))}
                />
              </div>
            )}

          </div>
          <br />

          {!(
            (values.boxSizes && values.packagePreDefinedWeight && values.packagingType == PRE_DEFINED_BOX) ||
            (values.packageLength &&
              values.packageWidth &&
              values.packageHeight &&
              values.packageWeight &&
              values.packagingType == CUSTOM_BOX)
          ) || addressCheck ? (
            <span className={css.error}>
              <FormattedMessage
                id="EditListingDeliveryForm.errorText"
                values={{ TabName: <b>Delivery</b> }}
              />
            </span>
          ) : null} <br />

          <div className={css.buttonWrapper}>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress || (validateAddressInProgress && !saveAsDraft)}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>

            {
              isPendingApproval ? <Button
                className={css.submitApprovalButton}
                type="button"
                inProgress={saveAsDraft == APPROVAL}
                disabled={submitDisabled}
                onClick={() => {
                  setSaveAsDraft(APPROVAL);
                  saveDraftFunction().then((res) => {
                    handlePublishListing(listing?.id);
                    setSaveAsDraft(null);
                  })
                }}
              >
                <FormattedMessage id="EditListingDeliveryForm.submitForApproval" />
              </Button> : null
            }

            <Button
              className={css.submitDraftButton}
              type="button"
              inProgress={saveAsDraft == LISTING_STATE_DRAFT}
              disabled={submitDisabled}
              onClick={() => {
                setSaveAsDraft(LISTING_STATE_DRAFT);
                saveDraftFunction().then((res) => {
                  history.push('/listings')
                  setSaveAsDraft(null)
                })
              }}
            >
              <FormattedMessage id="EditListingDeliveryForm.saveAsDraft" />
            </Button>
          </div>
        </Form>
      );
    }}
  />
);

EditListingDeliveryFormComponent.defaultProps = {
  selectedPlace: null,
  fetchErrors: null,
  formId: 'EditListingDeliveryForm',
  hasStockInUse: true,
};

EditListingDeliveryFormComponent.propTypes = {
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  selectedPlace: propTypes.place,
  marketplaceCurrency: string.isRequired,
  hasStockInUse: bool,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingDeliveryFormComponent);
